import { Navigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from '../hooks/usePermissions'

// First page a user can actually open, in the order they'd most likely want
// it. Used when a user has no DASHBOARD.VIEW so /app (which the catch-all
// route also redirects to) doesn't dead-end on an "Access Denied" page whose
// only button points back at /app.
const LANDING_CANDIDATES: Array<{ module: string; to: string }> = [
  { module: 'SPACES', to: '/spaces' },
  { module: 'LEADS', to: '/app/leads' },
  { module: 'DEALS', to: '/app/deals' },
  { module: 'QUOTATIONS', to: '/app/sales/quotations' },
  { module: 'INVOICE_REQUESTS', to: '/app/sales/invoice-requests' },
  { module: 'PROFORMA_INVOICES', to: '/app/sales/proforma-invoices' },
  { module: 'ACCOUNTS', to: '/app/sales/milestones' },
  { module: 'SALES_DOCS', to: '/sales-docs' },
  { module: 'PEOPLE', to: '/app/people' },
]

// /app is the Overview. Requires DASHBOARD.VIEW (the sidebar item already
// did, but the route itself was open to every authenticated user).
export function DashboardRoute({ children }: { children: React.ReactNode }) {
  const { isLoading } = useAuth()
  const { can } = usePermissions()

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (can('DASHBOARD', 'VIEW')) return <>{children}</>

  const landing = LANDING_CANDIDATES.find((c) => can(c.module, 'VIEW'))
  // /app/profile has no permission gate, so this can never loop back here.
  return <Navigate to={landing?.to ?? '/app/profile'} replace />
}
